import { handleDropdownInputListeners } from '@tesla/design-system';
import { iconChevronSmall180, iconCloseSmall } from '@tesla/design-system-icons';
import cx from 'classnames';
import React, { useEffect, useRef } from 'react';
import { useId } from '../hooks/use-id.js';
import { FormInputSearch, Icon, IconButton, ListBox, Tooltip, useFilterState, } from '../index.js';
import { toLabel, useListBoxState } from '../listbox/index.js';
import { toValue } from '../listbox/utils.js';
import { useDropdownState } from './use-dropdown-state.js';
export const FormInputDropdown = ({ className, defaultSelected, disabled, enableSearch: externalEnableSearch, id, inputRef, leading, listboxProps, multipleSelectedLabel = 'Selected', multipleSelectedLabelFull, name, readOnly, onChange, onClear: externalOnClear, onClick, onKeyDown, onOptionSelect: externalOnOptionSelect, open: externalOpen, options, placeholder, searchProps, selected: externalSelected, trailing, tooltipProps, variant = 'default', ...rest }) => {
    // If not explicitly set, add a search to the dropdown when
    // there's more than just a few options
    const enableSearch = externalEnableSearch !== null && externalEnableSearch !== void 0 ? externalEnableSearch : (options && options.length > 5);
    const fallbackState = useDropdownState(defaultSelected);
    const selected = externalSelected || fallbackState.selected;
    const multiple = Array.isArray(selected);
    const onOptionSelect = externalOnOptionSelect || fallbackState.onOptionSelect;
    const onClear = externalOnClear || fallbackState.onClear;
    const expanded = fallbackState.expanded;
    const setExpanded = fallbackState.setExpanded;
    const toggleExpanded = fallbackState.toggleExpanded;
    const listBoxState = useListBoxState(selected);
    /**
     * Inner search field
     */
    const { filterValue, filteredOptions, setFilterValue } = useFilterState({
        options,
    });
    const uId = useId();
    const htmlId = id || uId;
    const listboxRef = useRef(null);
    const containerRef = useRef(null);
    const tooltipRef = useRef(null);
    const searchRef = useRef(null);
    const fallbackRef = useRef(null);
    const ref = inputRef || fallbackRef;
    // When selected, update the search value with that option's label
    const handleOptionSelect = (option) => {
        var _a;
        onOptionSelect(option);
        listBoxState.onOptionSelect(option);
        if (!multiple) {
            setExpanded(false);
            (_a = ref.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    };
    const clearSearch = () => {
        if (enableSearch)
            setFilterValue('');
    };
    /**
     * Vanilla Listeners:
     * - Manage all the various event listeners in vanilla :)
     */
    useEffect(() => {
        if (!readOnly) {
            const handleSetExpanded = (expanded) => {
                var _a;
                setExpanded(expanded);
                setFilterValue('');
                if (expanded) {
                    // We need a setTimeout here to make sure the search focuses properly after the dropdown is expanded.
                    // Wrapping the focus call in setTimeout ensures that it occurs after other synchronous operations, making it work as intended.
                    setTimeout(() => {
                        var _a;
                        (_a = searchRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                    });
                }
                else {
                    (_a = ref.current) === null || _a === void 0 ? void 0 : _a.focus();
                }
            };
            const cleanup = handleDropdownInputListeners({
                clearInput: onClear,
                container: containerRef === null || containerRef === void 0 ? void 0 : containerRef.current,
                input: ref === null || ref === void 0 ? void 0 : ref.current,
                listbox: listboxRef === null || listboxRef === void 0 ? void 0 : listboxRef.current,
                // @ts-expect-error - 2 kinds of MouseEvent
                onClick,
                // @ts-expect-error - 2 kinds of KeyboardEvent
                onKeyDown,
                searchInput: searchRef === null || searchRef === void 0 ? void 0 : searchRef.current,
                setExpanded: handleSetExpanded,
                tooltip: tooltipRef === null || tooltipRef === void 0 ? void 0 : tooltipRef.current,
                toggleExpanded: () => handleSetExpanded(!expanded),
            });
            return cleanup;
        }
        return;
    }, [
        readOnly,
        containerRef,
        ref,
        listboxRef,
        tooltipRef,
        expanded,
        setExpanded,
        toggleExpanded,
        onClick,
        onKeyDown,
        onClear,
        setFilterValue,
    ]);
    if (!options) {
        return null;
    }
    let selectedLabel;
    if (multiple) {
        if (selected.length > 1) {
            selectedLabel = multipleSelectedLabelFull !== null && multipleSelectedLabelFull !== void 0 ? multipleSelectedLabelFull : `${selected.length} ${multipleSelectedLabel}`;
        }
        else {
            selectedLabel = toLabel(options.find((opt) => toValue(selected[0]) === toValue(opt)));
        }
    }
    else {
        selectedLabel = toLabel(options.find((opt) => toValue(selected) === toValue(opt)));
    }
    const hideClear = !(multiple && selected.length > 1);
    const open = externalOpen !== null && externalOpen !== void 0 ? externalOpen : (expanded && !disabled);
    const inputValue = multiple
        ? selected.map((item) => toValue(item)).toString()
        : toValue(selected);
    // Only pass props related to the attached listbox/dropdown if the input is not readOnly
    const writableProps = !readOnly
        ? {
            'aria-activedescendant': enableSearch ? undefined : listBoxState.activeDescendant,
            'aria-controls': enableSearch ? undefined : `${htmlId}-listbox`,
            'aria-expanded': open,
            'aria-haspopup': 'listbox',
            role: 'combobox',
        }
        : {};
    return (React.createElement("div", { className: cx('tds-form-input', {
            [`tds-form-input--${variant}`]: variant,
            'tds-form-input--readonly': readOnly,
            'tds--disabled': disabled,
        }, className), ref: containerRef },
        React.createElement("div", { className: "tds-form-input-leading" },
            leading,
            React.createElement("span", { className: cx('tds-chip', 'tds-chip--unencapsulated', {
                    'tds-placeholder': !selectedLabel,
                }) },
                React.createElement("span", { className: "tds-chip-text" }, selectedLabel || placeholder),
                !readOnly && (React.createElement(IconButton, { className: "tds-dropdown-clear-btn", disabled: disabled, size: "small", style: hideClear ? { display: 'none' } : undefined, tabIndex: -1 },
                    React.createElement(Icon, { data: iconCloseSmall }))))),
        React.createElement("input", { ...writableProps, autoComplete: "off", className: "tds-form-input-dropdown", disabled: disabled, id: htmlId, multiple: multiple, name: name, onChange: onChange, placeholder: placeholder, 
            // The actual input here is always readOnly as the value is solely controlled by the ListBox
            readOnly: true, ref: ref, value: inputValue, ...rest }),
        React.createElement("div", { className: "tds-form-input-trailing" },
            trailing,
            !readOnly && (React.createElement(Icon, { className: open ? 'tds-icon--flip-x' : '', data: iconChevronSmall180, rotate: 180 }))),
        !readOnly && (React.createElement(Tooltip, { align: (tooltipProps === null || tooltipProps === void 0 ? void 0 : tooltipProps.align) || variant === 'inline' ? 'start' : undefined, open: open, orientation: "down", tooltipRef: tooltipRef, width: variant !== 'inline' ? 'full' : undefined, ...tooltipProps, className: cx('tds-dropdown', tooltipProps === null || tooltipProps === void 0 ? void 0 : tooltipProps.className) },
            enableSearch && (React.createElement("div", { className: "tds-listbox-search" },
                React.createElement(FormInputSearch, { "aria-activedescendant": listBoxState.activeDescendant, "aria-controls": `${htmlId}-listbox`, "aria-expanded": open, autoComplete: "off", inputRef: searchRef, onChange: (e) => setFilterValue(e.target.value), onClear: clearSearch, role: "combobox", value: filterValue, variant: "open", ...searchProps }))),
            React.createElement(ListBox, { activeDescendant: listBoxState.activeDescendant, "aria-labelledby": htmlId, containerRef: containerRef, id: `${htmlId}-listbox`, inputRef: enableSearch ? searchRef : ref, listboxRef: listboxRef, onActiveDescendantChange: listBoxState.onActiveDescendantChange, onOptionSelect: handleOptionSelect, options: filteredOptions, selected: selected, ...listboxProps })))));
};
